import React, { MouseEvent as ReactMouseEvent, useMemo, useState } from 'react';
import cn from 'classnames';
import Header from '@components/Header';
import {
  PenIcon,
  ActionGroupsIcon,
  ActionMergeIcon,
  ActionNoteIcon,
  ActionArchiveIcon,
  ActionStarredIcon,
  ShareActionIcon,
  ReminderAlarmClockIcon,
} from '@components/Icons';
import styles from './ContactActions.module.scss';
import { buildSocial } from '@components/Contact/Social';
import Contact from '@models/Contact';
import { useDispatch, useSelector } from 'react-redux';
import { ReconnectIcon } from '@components/Icons/Reconnect';
import {
  modalStateSelector,
  openModal,
  selectActiveModalType,
  startNewNote,
  startNewReminder,
} from '@actions/ui';
import {
  archiveContact,
  toggleStarredStatus,
} from '@views/CommandBar/actionsBySection/contactActions';
import { useLocation, useNavigate } from 'react-router-dom';
import { DropdownMenuOption } from '@app/types';
import DropdownMenu from '@components/Menus/DropdownMenu';
import { selectContactShareStatus } from '@reducers/data/shareStatuses/contacts';
import { clearNewConversation } from '@actions/nexus';
import { NexusFlowerIcon } from '@components/Icons/AIFlower';
import { useAuthContext } from '@hooks/use-auth-context';
import ContactOptionsMenu from './ContactOptionsMenu';
import { inviteContact } from '@actions/contacts';
import { Dispatch } from 'redux';

interface ActionOptionsConfig {
  contact: Contact;
  setMenuOpen?: React.Dispatch<boolean>;
}

const contactPersonOption = ({ contact, setMenuOpen }: ActionOptionsConfig) => {
  const IconComponent = (props) => (
    <ContactOptionsMenu
      contactID={contact.id}
      onOpen={() => setMenuOpen?.(true)}
      onClose={() => setMenuOpen?.(false)}
      menuClassName={styles.contactOptionsMenu}
      {...props}
    />
  );

  return {
    title: 'Contact',
    shortcut: 'c',
    IconComponent,
  };
};

const confirmContactInvitation = (contact: Contact, dispatch: Dispatch) => {
  window.app.confirm(
    {
      title: 'Invite to Clay',
      message: `We will email ${contact.displayName} a personalized invitation to Clay.`,
      action: 'Send Invite',
    },
    (confirmed) => {
      if (confirmed) {
        dispatch(inviteContact(contact));
      }
    },
  );
};

const useContactActionOptions = ({ contact, setMenuOpen }: ActionOptionsConfig) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { contactIsRestricted, getEntitlementValue } = useAuthContext();
  const activeModalType = useSelector(selectActiveModalType);
  const contactShareStatus = useSelector(selectContactShareStatus(contact?.id));
  const canEditContact = !getEntitlementValue('contacts.edit.disabled');

  const actionsInHeader = useMemo(() => {
    const actions: {
      title: string;
      shortcut?: string;
      IconComponent?: (props: any) => JSX.Element;
      onClick?: (e: ReactMouseEvent) => void;
      className?: string;
      asButton?: boolean;
      tooltip?: boolean;
      active?: boolean;
      disabled?: boolean;
    }[] = [
      contact.email &&
        !contact.isClayUser &&
        !contact.isSharedContact && {
          title: contact.invited ? 'Invited' : 'Invite',
          onClick: () => confirmContactInvitation(contact, dispatch),
          asButton: true,
          tooltip: false,
          className: cn(styles.btn, 'btn-blue'),
          disabled: contact.invited,
        },
      !contactIsRestricted(contact) && contactPersonOption({ contact, setMenuOpen }),
      !contactIsRestricted(contact) && {
        title: 'Set Reminder',
        IconComponent: (props) => <ReminderAlarmClockIcon hoverOpacity {...props} />,
        className: styles.reminderAction,
        onClick: (e: ReactMouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          dispatch(startNewReminder);
        },
        shortcut: 'r',
      },
      !contactIsRestricted(contact) && {
        title: 'Set Reconnect Cadence',
        shortcut: 'h',
        IconComponent: (props) => (
          <ReconnectIcon contact={contact} width="22" height="22" {...props} />
        ),
        onClick: () => dispatch(openModal('reconnect')),
        className: styles.reconnectAction,
      },
      !contactIsRestricted(contact) && {
        title: 'Add Note',
        shortcut: 'n',
        IconComponent: (props) => <ActionNoteIcon width={23} height={23} {...props} />,
        onClick: (e: ReactMouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          dispatch(startNewNote);
        },
      },
      !contactIsRestricted(contact) &&
        canEditContact && {
          title: 'Edit other details',
          shortcut: 'e',
          onClick: () => navigate(`/contact/${contact.id}/form`),
          IconComponent: (props) => <PenIcon width="22" height="22" {...props} />,
          className: styles.editAction,
        },
      contactIsRestricted(contact) && {
        title: 'Star Person',
        shortcut: 'S',
        IconComponent: (props) => (
          <ActionStarredIcon strokeWidth="1.9" contact={contact} {...props} />
        ),
        onClick: () => toggleStarredStatus(contact.id),
      },
      contactIsRestricted(contact) && {
        title: 'Add to Group',
        shortcut: 'L',
        onClick: () => dispatch(openModal('addToGroups')),
        IconComponent: ActionGroupsIcon,
        active: activeModalType == 'addToGroups',
      },
      contactIsRestricted(contact) && {
        title: 'Merge Person',
        shortcut: 'm',
        onClick: () => dispatch(openModal('merge')),
        IconComponent: ActionMergeIcon,
        active: activeModalType == 'merge',
      },
      contactIsRestricted(contact) && {
        title: 'Archive Person',
        shortcut: '#',
        onClick: () => archiveContact(contact, location, navigate, dispatch),
        IconComponent: ActionArchiveIcon,
      },
    ].filter((x) => !!x);

    return actions;
  }, [contact, activeModalType, contactShareStatus, navigate]);

  const actionsInOverflowMenu = useMemo(() => {
    const actions: DropdownMenuOption[] = [
      {
        title: `Ask Nexus about ${contact.displayName}`,
        action: () => {
          dispatch(clearNewConversation);
          navigate('/nexus', { state: { contactID: contact.id } });
        },
        icon: (props) => <NexusFlowerIcon strokeWidth={1.8} {...props} />,
      },
      {
        title: 'Star Person',
        keyboard: 's',
        icon: (props) => <ActionStarredIcon strokeWidth="1.9" contact={contact} {...props} />,
        action: () => toggleStarredStatus(contact.id),
      },
      {
        title: 'Add to Group',
        keyboard: 'L',
        action: () => dispatch(openModal('addToGroups')),
        icon: ActionGroupsIcon,
      },
      {
        title: 'Merge Person',
        keyboard: 'm',
        action: () => dispatch(openModal('merge')),
        icon: ActionMergeIcon,
      },
      {
        title: 'Archive Person',
        keyboard: '#',
        action: () => archiveContact(contact, location, navigate, dispatch),
        icon: ActionArchiveIcon,
      },
      {
        title: 'Share Person',
        action: () => dispatch(openModal('shareContact')),
        icon: ShareActionIcon,
      },
    ];

    return actions;
  }, [contact, activeModalType, navigate]);

  return [actionsInHeader, actionsInOverflowMenu];
};

export function profileIsEmpty(contact: Contact) {
  return (
    (contact.bio || '').length === 0 &&
    (contact.byline || '').length === 0 &&
    (contact.organizations || []).length === 0 &&
    (contact.educations || []).length === 0 &&
    (contact.interests || []).length === 0 &&
    (contact.websites || []).length === 0 &&
    buildSocial(contact).length === 0
  );
}

function HeaderActions({ contact, isSelf }) {
  const { contactIsRestricted } = useAuthContext();
  const { isOpen: modalIsOpen } = useSelector(modalStateSelector);
  const [menuOpen, setMenuOpen] = useState(false);
  const [actionsInHeader, actionsInOverflowMenu] = useContactActionOptions({
    contact,
    setMenuOpen,
  });
  return (
    !isSelf && (
      <>
        {actionsInHeader.map((props, i) => (
          <Header.Action
            key={i}
            large
            hover
            tooltip={!modalIsOpen}
            hideTooltip={menuOpen || modalIsOpen}
            {...props}
          />
        ))}
        {!contactIsRestricted(contact) && (
          <DropdownMenu
            options={actionsInOverflowMenu}
            triggerClassName={styles.overflowMenuToggle}
          />
        )}
      </>
    )
  );
}

export default function Actions({ contact, isSelf }) {
  return (
    <Header
      className={styles.header}
      goBack
      actions={<HeaderActions isSelf={isSelf} contact={contact} />}
    >
      <span className={styles.contactName}>{contact.displayName}</span>
    </Header>
  );
}
